import {
  getAuth,
  onAuthStateChanged,
  signInAnonymously,
  signOut,
  Unsubscribe,
  User,
} from 'firebase/auth';
import { getApps, initializeApp } from 'firebase/app';
import { firebaseConfig } from '../utils/APIConfig';

if (!getApps().length) {
  initializeApp(firebaseConfig);
}

class AuthService {
  private static instance: AuthService;
  private auth = getAuth();
  private authListener?: Unsubscribe;
  private userId: string | null = null;
  private initializing = false;

  private constructor() {}

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public initialize(onUserChanged: (uid: string | null) => void): () => void {
    if (!this.authListener) {
      this.authListener = onAuthStateChanged(
        this.auth,
        async (user) => {
          await this.handleAuthState(user, onUserChanged);
        },
        (error) => {
          console.error('Firebase auth listener error:', error);
          this.userId = null;
          onUserChanged(null);
        }
      );

      // Trigger initial sign-in if needed
      if (!this.auth.currentUser && !this.initializing) {
        this.initializing = true;
        signInAnonymously(this.auth)
          .catch((error) => {
            console.error('Failed to sign in anonymously:', error);
            this.userId = null;
            onUserChanged(null);
          })
          .finally(() => {
            this.initializing = false;
          });
      } else if (this.auth.currentUser) {
        this.userId = this.auth.currentUser.uid;
        onUserChanged(this.userId);
      }
    } else {
      onUserChanged(this.userId);
    }

    return () => {
      this.authListener?.();
      this.authListener = undefined;
    };
  }

  private async handleAuthState(
    user: User | null,
    onUserChanged: (uid: string | null) => void
  ) {
    if (user) {
      this.userId = user.uid;
      onUserChanged(user.uid);
      return;
    }

    this.userId = null;
    onUserChanged(null);

    if (!this.initializing) {
      this.initializing = true;
      try {
        const credential = await signInAnonymously(this.auth);
        this.userId = credential.user.uid;
        onUserChanged(this.userId);
      } catch (error) {
        console.error('Anonymous sign-in failed:', error);
        this.userId = null;
        onUserChanged(null);
      } finally {
        this.initializing = false;
      }
    }
  }

  public getUserId(): string | null {
    return this.userId ?? this.auth.currentUser?.uid ?? null;
  }

  /**
   * Returns a Firebase ID token for the current user, signing in
   * anonymously first if there is no current user.
   */
  public async getIdToken(): Promise<string | null> {
    try {
      if (!this.auth.currentUser) {
        const credential = await signInAnonymously(this.auth);
        this.userId = credential.user.uid;
      }
      return (await this.auth.currentUser?.getIdToken()) ?? null;
    } catch (error) {
      console.error('Failed to get Firebase ID token:', error);
      return null;
    }
  }

  public isAuthenticated(): boolean {
    return this.getUserId() !== null;
  }

  public async clearUserId(): Promise<void> {
    try {
      await signOut(this.auth);
      this.userId = null;
    } catch (error) {
      console.error('Failed to clear user ID:', error);
    }
  }

  public async regenerateUserId(): Promise<string | null> {
    try {
      await signOut(this.auth);
      const credential = await signInAnonymously(this.auth);
      this.userId = credential.user.uid;
      return this.userId;
    } catch (error) {
      console.error('Failed to regenerate user ID:', error);
      this.userId = null;
      return null;
    }
  }
}

export default AuthService;
